/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useCallback, useMemo } from 'react';

import classnames from 'classnames';

import { useMediaQuery } from '@mui/material';

import LinkButton from 'components/uiLibrary/LinkButton';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import Typography from 'components/uiLibrary/Typography';

import useTracking from 'components/Globals/Analytics';
import EntityName from 'components/Globals/EntityName';
import useQuickView, { QUICK_VIEW_TYPES } from 'components/Globals/Layout/QuickView';

import { ENTITY_TYPE_MAP, TP } from 'constants/index';

import ImpressionTracker from 'utils/components/impressionTracker';

import { useTranslation } from 'src/i18n';

import { ENTITY_TYPES } from 'operabase-router/constants';

import CreditsWorks from '../CreditsWorks';
import GenreTags from '../GenreTags';
import ProductionPeriod from '../ProductionPeriod';
import classes from '../ProSlugCollapsed/ProSlugCollapsed.module.scss';
import { DirectorConductor } from '../renderers';

const ProductionSlugInline = ({ isExpanded, production, trackingData, onClick, entityType, entityId }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const isMobile = useMediaQuery('(max-width: 1000px)');
  const track = useTracking();
  const { showQuickView } = useQuickView();

  const openQuickPreview = useCallback(
    ({ productionId }) => {
      showQuickView({
        type: QUICK_VIEW_TYPES.PRODUCTION,
        data: {
          entityId: productionId,
          quickViewContextId: entityId,
        },
      });
    },
    [entityId], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const { conductor, director, producer, id, crewCount, castCount, instrumentalCount } = production || {};

  const completeTrackingData = {
    ...trackingData,
    entityId: production?.id,
    entityName: production?.name,
    entityType: ENTITY_TYPES.PRODUCTION,
  };

  const icon = useMemo(() => {
    if (isMobile) {
      return 'chevron_right';
    }

    if (isExpanded) {
      return 'keyboard_arrow_up';
    }

    return 'expand_more';
  }, [isExpanded, isMobile]);

  const handleOnClick = e => {
    if (isMobile) {
      openQuickPreview({ productionId: id });
    } else {
      onClick(e);

      track.click({
        ...completeTrackingData,
        meta: {
          isOpen: isExpanded,
        },
      });
    }
  };

  return (
    <ImpressionTracker data={completeTrackingData} className={classes.root}>
      <div className={classnames(classes.psc, classes.inline)} onClick={handleOnClick}>
        {!isMobile && (
          <div className={classes.psc__expandBtn}>
            <LinkButton
              styles={{ root: classes.arrowIcon }}
              variant="text"
              {...(isMobile
                ? { leftIcon: <SpriteIcon icon={icon} size={12} /> }
                : { rightIcon: <SpriteIcon icon={icon} size={12} /> })}
              disableHover
              disableUnderline
              skipTracking
            />
          </div>
        )}
        <div className={classes.psc__block}>
          <div className={classes.psc__block_left}>
            <div className={classes.tags}>
              <GenreTags production={production} showStagingType={false} showTags={false} truncateWorkType />
            </div>
            <div className={classes.creditsWorks}>
              <CreditsWorks
                production={production}
                entityId={entityId}
                trackingData={trackingData}
                entityType={entityType}
                isInLineView
              />
            </div>
          </div>
          <div className={classes.psc__block_right}>
            <div className={classes.periodDates}>
              <div className={classes.period}>
                <ProductionPeriod production={production} trackingData={trackingData} />
              </div>
            </div>
            <div className={classes.orgCastCrewWrapper}>
              <div className={classes.orgVenues}>
                <div className={classes.org}>
                  {producer?.id && (
                    <div>
                      <Typography size="12" weight="bold" className={classes.producerName}>
                        <EntityName
                          key={producer.id}
                          entityType={ENTITY_TYPE_MAP[producer?.type]}
                          entity={producer}
                          isRaw
                          trackingData={trackingData}
                        />
                        {production?.isProducerDebut && (
                          <Typography size={11} color="secondary" className={classes.producerDebut}>
                            (PD)
                          </Typography>
                        )}
                      </Typography>
                    </div>
                  )}
                </div>
              </div>
              <div className={classes.crewCast}>
                <DirectorConductor
                  directors={director}
                  conductors={conductor}
                  t={t}
                  crewCount={crewCount}
                  castCount={castCount}
                  trackingData={trackingData}
                />
                <div className={classes.genreTags}>
                  <GenreTags
                    production={production}
                    showGenre={false}
                    showStagingType={false}
                    showTags
                    truncateWorkType
                  />
                </div>
                {instrumentalCount > 0 && (
                  <Typography size="12" color="tertiary" className={classes.instrumentalCount}>
                    {t(`${TP}.FN_INDUSTRY_PRO_INSTRUMENTALISTS_LABEL`)} <strong>({instrumentalCount})</strong>
                  </Typography>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {isMobile && <div className={classes.divider}></div>}
    </ImpressionTracker>
  );
};

export default ProductionSlugInline;
